/** @fileoverview Prepares images for upload and describes attachment limits and errors. */

import { ApiError } from '../services/platformAuth';

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_BYTES = 750 * 1024;
const MAX_IMAGE_SIDE = 1568;

const encode = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

/** Shrinks an image to a JPEG under the size limit, scaling down until it fits. */
export async function prepareImage(file: File): Promise<Blob> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) throw new Error('UNSUPPORTED_IMAGE');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('UNREADABLE_IMAGE');
  });
  try {
    let scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('UNREADABLE_IMAGE');
      // JPEG has no alpha; transparent pixels would turn black.
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await encode(canvas, attempt < 3 ? 0.85 : 0.7);
      if (blob && blob.size <= MAX_IMAGE_BYTES) return blob;
      scale *= 0.8;
    }
  } finally {
    bitmap.close();
  }
  throw new Error('IMAGE_TOO_LARGE');
}

/** Turns a failed prepare or upload into a message for the user. */
export function imageErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 429) return error.message;
    if (error.status === 404) return 'This conversation is no longer available.';
    if (error.status && error.status < 500 && error.status !== 401) return error.message;
    return 'The image could not be uploaded. Try again.';
  }
  switch (error instanceof Error ? error.message : '') {
    case 'UNSUPPORTED_IMAGE':
      return 'Use a PNG, JPEG or WebP image.';
    case 'UNREADABLE_IMAGE':
      return 'This image could not be read.';
    case 'IMAGE_TOO_LARGE':
      return 'This image is too large, even after shrinking.';
    default:
      return 'The image could not be uploaded. Try again.';
  }
}
